import { create } from 'zustand';
import { ecommerceApi } from '@/services/api';

interface EcommercePrefetchState {
  // Cached data
  stats: any | null;
  orders: any | null;
  noSales: any | null;

  // Loading states
  statsLoading: boolean;
  ordersLoading: boolean;
  noSalesLoading: boolean;

  // Timestamps for staleness check
  statsFetchedAt: number;
  ordersFetchedAt: number;

  // Actions
  prefetch: () => void;
  invalidate: () => void;
}

const STALE_MS = 5 * 60 * 1000; // 5 minutes (matches backend Redis TTL)

export const useEcommerceStore = create<EcommercePrefetchState>()((set, get) => ({
  stats: null,
  orders: null,
  noSales: null,

  statsLoading: false,
  ordersLoading: false,
  noSalesLoading: false,

  statsFetchedAt: 0,
  ordersFetchedAt: 0,

  prefetch: () => {
    const now = Date.now();
    const state = get();

    // Don't re-fetch if data is fresh
    if (state.stats && now - state.statsFetchedAt < STALE_MS) return;

    // Don't double-fetch
    if (state.statsLoading) return;

    set({ statsLoading: true, ordersLoading: true, noSalesLoading: true });

    // Fetch all three in parallel
    ecommerceApi.stats()
      .then((res) => set({ stats: res.data, statsLoading: false, statsFetchedAt: Date.now() }))
      .catch(() => set({ statsLoading: false }));

    ecommerceApi.getOrders(1, 10)
      .then((res) => set({ orders: res.data, ordersLoading: false, ordersFetchedAt: Date.now() }))
      .catch(() => set({ ordersLoading: false }));

    ecommerceApi.getNoSalesProducts(1, 10)
      .then((res) => set({ noSales: res.data, noSalesLoading: false }))
      .catch(() => set({ noSalesLoading: false }));
  },

  invalidate: () => {
    set({ stats: null, orders: null, noSales: null, statsFetchedAt: 0, ordersFetchedAt: 0 });
  },
}));
